import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  Car, 
  FileText,
  Phone,
  Search,
  CheckCircle,
  ChevronRight,
  Users,
  Shield,
  Clock,
  HelpCircle
} from "lucide-react";

export const HowItWorks = () => {
  return (
    <div className="hiw-page">
      <Helmet>
        <title>How It Works | Moto Money Finance</title>
        <meta name="description" content="Learn how the vehicle financing process works at Moto Money Finance. From application to driving away, we make it simple." />
      </Helmet>

      <header className="site-header">
        <div className="header-container">
          <Link to="/" className="logo" data-testid="logo">
            <Car size={32} />
            <span>Moto Money<strong>Finance</strong></span>
          </Link>
          <nav className="nav-links">
            <Link to="/faq" className="nav-link"><HelpCircle size={18} /> FAQ</Link>
            <Link to="/apply" className="btn-apply" data-testid="header-apply-btn">Apply Now</Link>
          </nav>
        </div>
      </header>

      <section className="hiw-hero">
        <div className="hiw-hero-content">
          <h1>How It Works</h1>
          <p>Getting into your next vehicle is simpler than you think. Here's what to expect when you work with Moto Money Finance.</p>
        </div>
      </section>

      <section className="hiw-steps-section">
        <div className="hiw-container">
          {/* Step 1 */}
          <div className="hiw-step" data-testid="step-1">
            <div className="step-indicator">
              <div className="step-number">1</div>
              <div className="step-line"></div>
            </div>
            <div className="step-content">
              <div className="step-icon"><FileText size={24} /></div>
              <h2>Apply Online</h2>
              <p>Fill out our simple online application. It takes just a few minutes and helps us understand your needs and situation.</p>
              <ul>
                <li><CheckCircle size={16} /> Share your contact information</li>
                <li><CheckCircle size={16} /> Tell us about your vehicle preferences</li>
                <li><CheckCircle size={16} /> Provide basic financial information</li>
              </ul>
            </div>
          </div>

          {/* Step 2 */}
          <div className="hiw-step" data-testid="step-2">
            <div className="step-indicator">
              <div className="step-number">2</div>
              <div className="step-line"></div>
            </div>
            <div className="step-content">
              <div className="step-icon"><Phone size={24} /></div>
              <h2>Team Follow-Up</h2>
              <p>One of our team members will contact you to discuss your application and answer any questions you have.</p>
              <ul>
                <li><CheckCircle size={16} /> We'll review your application</li>
                <li><CheckCircle size={16} /> Discuss your vehicle needs and budget</li>
                <li><CheckCircle size={16} /> Explain available financing options</li>
              </ul>
            </div>
          </div>

          {/* Step 3 */}
          <div className="hiw-step" data-testid="step-3">
            <div className="step-indicator">
              <div className="step-number">3</div>
              <div className="step-line"></div>
            </div>
            <div className="step-content">
              <div className="step-icon"><Search size={24} /></div>
              <h2>Review Your Options</h2>
              <p>We'll work with you to explore available vehicles and financing options that fit your needs.</p>
              <ul>
                <li><CheckCircle size={16} /> Browse vehicles that match your criteria</li>
                <li><CheckCircle size={16} /> Review financing terms and rates</li>
                <li><CheckCircle size={16} /> Ask questions and get clear answers</li>
              </ul>
            </div>
          </div>

          {/* Step 4 */}
          <div className="hiw-step" data-testid="step-4">
            <div className="step-indicator">
              <div className="step-number">4</div>
            </div>
            <div className="step-content">
              <div className="step-icon"><CheckCircle size={24} /></div>
              <h2>Complete Paperwork</h2>
              <p>Once you've found the right vehicle and financing, we'll help you complete the necessary paperwork.</p>
              <ul>
                <li><CheckCircle size={16} /> Review and sign documents</li>
                <li><CheckCircle size={16} /> Finalize your financing</li>
                <li><CheckCircle size={16} /> Get ready to drive your new vehicle</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="hiw-why-section">
        <div className="hiw-container">
          <h2>Why Choose Moto Money Finance?</h2>
          <div className="why-grid">
            <div className="why-card">
              <Users size={32} />
              <h3>All Credit Situations</h3>
              <p>Whether you have excellent credit or are rebuilding, we work with Canadians in all credit situations.</p>
            </div>
            <div className="why-card">
              <Shield size={32} />
              <h3>Safe & Secure</h3>
              <p>Your information is protected. We never sell your data to third parties.</p>
            </div>
            <div className="why-card">
              <Clock size={32} />
              <h3>Fast Process</h3>
              <p>Our streamlined process gets you answers quickly so you can focus on finding your vehicle.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="hiw-delivery-section">
        <div className="hiw-container">
          <div className="delivery-card">
            <Car size={32} />
            <div>
              <h3>Delivery Options</h3>
              <p>Ask our team about delivery options for your community. We serve customers across Manitoba and northern communities. Contact us to discuss what options are available for your location.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="hiw-cta-section">
        <div className="hiw-container">
          <h2>Ready to Get Started?</h2>
          <p>Apply online today and take the first step toward your next vehicle.</p>
          <div className="cta-buttons">
            <Link to="/apply" className="btn btn-primary" data-testid="cta-apply-btn">Start Your Application <ChevronRight size={18} /></Link>
            <Link to="/faq" className="btn btn-secondary">Have Questions? See FAQ</Link>
          </div>
        </div>
      </section>

      <footer className="site-footer">
        <div className="footer-container">
          <div className="footer-brand">
            <Link to="/" className="footer-logo"><Car size={28} /><span>Moto Money<strong>Finance</strong></span></Link>
            <p>Driving Made Easy</p>
          </div>
          <div className="footer-links">
            <h4>Quick Links</h4>
            <Link to="/apply">Apply Now</Link>
            <Link to="/how-it-works">How It Works</Link>
            <Link to="/faq">FAQ</Link>
            <Link to="/privacy">Privacy Policy</Link>
          </div>
          <div className="footer-contact">
            <h4>Contact Us</h4>
            <a href="mailto:sales@motomoneyfinance.com">sales@motomoneyfinance.com</a>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Moto Money Finance. All rights reserved.</p>
          <p><Link to="/privacy">Privacy Policy</Link></p>
        </div>
      </footer>
    </div>
  );
};

export default HowItWorks;
